import { Modal, Form, Input, Select, Space, Typography, message } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { t } from 'ttag';

import { DatePicker } from 'common/components/DatePicker';
import Label from 'common/components/Label';
import SearchQuerySelect from 'common/components/SearchQuerySelect';

import { organizationSearchQueryOptions } from '../Organizations/queries';
import { usePackagesQuery } from '../Packages/queries';

import { useCreateTrialMutation } from './queries';

const { Title } = Typography;

const ERROR_MESSAGES_BY_CODE = () => ({
  '292a9a29-c548-4ea2-bcfe-3b0c130c5b52': t`The Event Code is already in use. Please enter a different code.`,
  '64ac0696-430f-463d-ae4a-583a37c6832e': t`Event Duration cannot exceed more than 7 days`,
});

const CreateModal = ({ visible, onClose }) => {
  const [form] = Form.useForm();

  const createTrialMutation = useCreateTrialMutation();
  const { data: packages = [], isLoading: packagesLoading } = usePackagesQuery({
    type: 'event_credit',
  });

  const handleSubmit = async ({ eventDates, ...values }) => {
    const payload = {
      ...values,
      eventStartDate: eventDates[0].format('YYYY-MM-DD'),
      eventEndDate: eventDates[1].format('YYYY-MM-DD'),
      salesforceOpportunityId: '',
    };

    createTrialMutation.mutate(payload, {
      onSuccess: () => {
        form.resetFields();
        onClose(true);
        message.success(t`Trial created successfully`);
      },
      onError: err => {
        const errorMessage =
          ERROR_MESSAGES_BY_CODE()[err?.errors?.[0]?.code] ??
          t`Unknown error occurred while creating trial event.`;

        message.error(errorMessage);
      },
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onClose(false);
  };

  // Auto-set end date when start date changes
  const handleStartDateChange = date => {
    const endDate = form.getFieldValue('eventEndDate');
    if (!endDate && date) {
      form.setFieldsValue({ eventEndDate: date });
    }
  };

  return (
    <Modal
      title={t`New Trial`}
      open={visible}
      onCancel={handleCancel}
      okButtonProps={{
        loading: createTrialMutation.isLoading,
        htmlType: 'submit',
        form: 'create-trial-form',
      }}
      cancelButtonProps={{ disabled: createTrialMutation.isLoading }}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        id="create-trial-form"
        layout="vertical"
        requiredMark={false}
        onFinish={handleSubmit}
        preserve={false}
        disabled={createTrialMutation.isLoading}
      >
        <Form.Item
          name="organizationId"
          label={<Label>{t`Organization`}</Label>}
          rules={[{ required: true, message: t`Organization is required` }]}
        >
          <SearchQuerySelect
            placeholder={t`Search and select organization`}
            getQueryOptions={organizationSearchQueryOptions}
            options={data =>
              data?.map(org => ({
                label: org.name,
                value: org.id,
              }))
            }
          />
        </Form.Item>

        <Form.Item
          name="blueprintId"
          label={<Label>{t`Package`}</Label>}
          rules={[{ required: true, message: t`Package is required` }]}
        >
          <Select
            placeholder={t`Select a package`}
            loading={packagesLoading}
            showSearch={{
              filterOption: (input, { label }) =>
                label.toLocaleLowerCase().includes(input.toLocaleLowerCase()),
            }}
            options={packages.map(pkg => ({ label: pkg.name, value: pkg.id }))}
          />
        </Form.Item>

        <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
          {t`Event Information`}
        </Title>

        <Form.Item
          name="eventName"
          label={<Label>{t`Event Name`}</Label>}
          rules={[{ required: true, message: t`Event Name is required` }]}
        >
          <Input placeholder={t`Enter event name`} />
        </Form.Item>

        <Form.Item
          name="eventDates"
          label={<Label>{t`Event Dates`}</Label>}
          rules={[{ required: true, message: t`Event Dates are required` }]}
        >
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            onChange={handleStartDateChange}
            disabledDate={current => current && current < moment().startOf('day')}
          />
        </Form.Item>

        <Form.Item label={<Label>{t`Event Code`}</Label>}>
          <Space.Compact style={{ display: 'flex' }}>
            <Space.Addon>https://www.eventmobi.com/TRIAL-</Space.Addon>
            <Form.Item
              name="eventShortcode"
              rules={[
                { required: true, message: t`Event Code is required` },
                { min: 3, message: t`Event Code must be at least 3 characters` },
                { max: 123, message: t`Event Code cannot exceed 123 characters` },
                {
                  pattern: /^[a-zA-Z0-9_-]+$/,
                  message: t`Event Code may only contain alpha-numeric characters, underscores, and dashes`,
                },
              ]}
              noStyle
            >
              <Input placeholder={t`Enter event code`} />
            </Form.Item>
          </Space.Compact>
        </Form.Item>
      </Form>
    </Modal>
  );
};

CreateModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateModal;
